/**
 * TemplateTopBar Component
 *
 * Top action bar for the template editor showing:
 * - Mobile menu toggle
 * - Duplicate actions when template is selected
 */

import React from 'react';
import {
  AutoAwesome as AutoAwesomeIcon,
  Business as OrgIcon,
  ContentCopy as DuplicateIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';

import { LAYOUT_DIMENSIONS } from '../constants/layout';

interface TemplateTopBarProps {
  /** Whether a template is selected */
  hasSelectedTemplate: boolean;
  /** Whether user can copy the template */
  canDuplicate: boolean;
  /** Whether sidebar is visible (mobile) */
  isSidebarVisible: boolean;
  /** Callback to toggle sidebar */
  onToggleSidebar: () => void;
  /** Callback to copy template */
  onDuplicate: () => void;
  /** Whether user can edit template with AI */
  canEditWithAI?: boolean;
  /** Callback to edit template with AI */
  onEditWithAI?: () => void;
  /** Whether edit with AI is loading */
  isEditWithAILoading?: boolean;
  /** Whether user can convert template to org scope */
  canConvertToOrg?: boolean;
  /** Callback to convert template to org scope */
  onConvertToOrg?: () => void;
  /** Whether template is org-scoped */
  isOrgTemplate?: boolean;
  /** Whether user can edit org template settings */
  canEditOrgSettings?: boolean;
  /** Whether template is locked (members cannot customize) */
  isLocked?: boolean;
  /** Callback to toggle lock state */
  onToggleLock?: () => void;
}

export const TemplateTopBar: React.FC<TemplateTopBarProps> = ({
  hasSelectedTemplate,
  canDuplicate,
  isSidebarVisible,
  onToggleSidebar,
  onDuplicate,
  canEditWithAI,
  onEditWithAI,
  isEditWithAILoading,
  canConvertToOrg,
  onConvertToOrg,
  isOrgTemplate,
  canEditOrgSettings,
  isLocked,
  onToggleLock,
}) => {
  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: LAYOUT_DIMENSIONS.TOP_BAR_HEIGHT,
        px: { xs: 2, sm: 3 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {/* Left Section - Mobile Menu Toggle */}
      <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
        <IconButton
          onClick={onToggleSidebar}
          aria-label={isSidebarVisible ? 'Hide menu' : 'Show menu'}
          sx={{ color: 'primary.main' }}
        >
          <MenuIcon />
        </IconButton>
      </Box>

      {/* Spacer for desktop */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }} />

      {/* Right Section - Template Actions */}
      {hasSelectedTemplate && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1 },
          }}
        >
          {canEditWithAI && (
            <Tooltip title="Edit with AI">
              <IconButton
                onClick={onEditWithAI}
                disabled={isEditWithAILoading}
                sx={{ color: 'primary.main' }}
                aria-label="Edit with AI"
              >
                {isEditWithAILoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <AutoAwesomeIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </Tooltip>
          )}

          {canDuplicate && (
            <Tooltip title="Duplicate template">
              <IconButton
                onClick={onDuplicate}
                sx={{ color: 'primary.main' }}
                aria-label="Duplicate template"
              >
                <DuplicateIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}

          {canConvertToOrg && (
            <Tooltip title="Share with Organization">
              <IconButton
                onClick={onConvertToOrg}
                sx={{ color: 'primary.main' }}
                aria-label="Share with Organization"
              >
                <OrgIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}

          {isOrgTemplate && canEditOrgSettings && (
            <Tooltip title={isLocked ? 'Template locked - members cannot customize' : 'Template unlocked - members can customize'}>
              <IconButton
                onClick={onToggleLock}
                sx={{ color: 'primary.main' }}
                aria-label={isLocked ? 'Unlock template' : 'Lock template'}
              >
                {isLocked ? <LockIcon sx={{ fontSize: 20 }} /> : <LockOpenIcon sx={{ fontSize: 20 }} />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
};
